import { Type, type Static } from "@sinclair/typebox";
import type { DashboardCards, DashboardCardMetric } from "./dashboard-cards.js";
export const QuoteHourlyCostSchema = Type.Object(
  {
    managementYear: Type.Integer(),
    asOf: Type.String({ format: "date" }),
    unitCost: Type.Union([Type.String(), Type.Null()]),
  },
  { additionalProperties: false },
);
export type QuoteHourlyCost = Static<typeof QuoteHourlyCostSchema>;

export const EconomicsQuerySchema = Type.Object(
  {
    managementYear: Type.Optional(
      Type.Integer({ minimum: 2000, maximum: 2200, default: 2026 }),
    ),
    status: Type.Optional(Type.String({ maxLength: 40 })),
    kind: Type.Optional(Type.String({ maxLength: 40 })),
    limit: Type.Optional(
      Type.Integer({ minimum: 1, maximum: 200, default: 50 }),
    ),
    month: Type.Optional(Type.Integer({ minimum: 1, maximum: 12 })),
  },
  { additionalProperties: false },
);

// The dashboard resolves its default year from management_years.is_current.
export const DashboardQuerySchema = Type.Object(
  {
    ...EconomicsQuerySchema.properties,
    managementYear: Type.Optional(
      Type.Integer({ minimum: 2000, maximum: 2200 }),
    ),
  },
  { additionalProperties: false },
);

export const QuoteRegisterQuerySchema = Type.Object(
  {
    managementYear: Type.Optional(
      Type.Integer({ minimum: 2000, maximum: 2200, default: 2026 }),
    ),
    search: Type.Optional(Type.String({ maxLength: 200 })),
    status: Type.Optional(Type.String({ maxLength: 40 })),
    origin: Type.Optional(
      Type.Union([
        Type.Literal("local"),
        Type.Literal("legacy"),
        Type.Literal("fic"),
        Type.Literal("job-aggregate"),
      ]),
    ),
    clientId: Type.Optional(Type.String({ format: "uuid" })),
    trash: Type.Optional(
      Type.Union([Type.Literal("active"), Type.Literal("deleted")]),
    ),
    limit: Type.Optional(
      Type.Integer({ minimum: 1, maximum: 200, default: 100 }),
    ),
  },
  { additionalProperties: false },
);

export type EconomicsQuery = Static<typeof EconomicsQuerySchema>;
export type QuoteRegisterQuery = Static<typeof QuoteRegisterQuerySchema>;

export const QuoteRegisterVisibilitySchema = Type.Object(
  {
    operationId: Type.String({ format: "uuid" }),
    version: Type.Integer({ minimum: 1 }),
    trashed: Type.Boolean(),
  },
  { additionalProperties: false },
);
export type QuoteRegisterVisibility = Static<
  typeof QuoteRegisterVisibilitySchema
>;

export interface QuoteChapter {
  key: string;
  description: string;
  details?: string | null;
}
const QuoteChapterSchema = Type.Object(
  {
    key: Type.String({ format: "uuid" }),
    description: Type.String({ minLength: 1, maxLength: 500 }),
    details: Type.Optional(
      Type.Union([Type.String({ maxLength: 10000 }), Type.Null()]),
    ),
  },
  { additionalProperties: false },
);

const JobQuoteLineWriteSchema = Type.Object(
  {
    chapterKey: Type.Optional(
      Type.Union([Type.String({ format: "uuid" }), Type.Null()]),
    ),
    hiddenFromCustomer: Type.Optional(Type.Boolean()),
    description: Type.String({ minLength: 1, maxLength: 500 }),
    details: Type.Optional(
      Type.Union([Type.String({ maxLength: 10000 }), Type.Null()]),
    ),
    quantity: Type.Number({ exclusiveMinimum: 0, maximum: 99999999 }),
    unit: Type.Optional(
      Type.Union([Type.String({ maxLength: 40 }), Type.Null()]),
    ),
    unitCost: Type.Number({ minimum: 0, maximum: 9999999999.9999 }),
    markupPercent: Type.Number({ minimum: -100, maximum: 999.9999 }),
    unitSalePrice: Type.Number({ minimum: 0, maximum: 9999999999.9999 }),
  },
  { additionalProperties: false },
);

export const JobQuoteSaveSchema = Type.Object(
  {
    intermediaryPercent: Type.Optional(
      Type.Number({ minimum: 0, maximum: 100 }),
    ),
    operationId: Type.String({ format: "uuid" }),
    jobVersion: Type.Integer({ minimum: 1 }),
    recordedCostBasis: Type.Optional(
      Type.Number({ minimum: 0, maximum: 999999999999.99 }),
    ),
    subject: Type.String({ minLength: 1, maxLength: 500 }),
    issuedOn: Type.String({ format: "date" }),
    validUntil: Type.Optional(
      Type.Union([Type.String({ format: "date" }), Type.Null()]),
    ),
    vatPercent: Type.Number({ minimum: 0, maximum: 100 }),
    recoveryAmount: Type.Optional(
      Type.Number({ minimum: 0, maximum: 9999999999.99 }),
    ),
    recoveryNote: Type.Optional(
      Type.Union([Type.String({ minLength: 3, maxLength: 1000 }), Type.Null()]),
    ),
    chapters: Type.Optional(
      Type.Array(QuoteChapterSchema, { minItems: 1, maxItems: 200 }),
    ),
    lines: Type.Array(JobQuoteLineWriteSchema, {
      minItems: 1,
      maxItems: 200,
    }),
  },
  { additionalProperties: false },
);

export type JobQuoteSave = Static<typeof JobQuoteSaveSchema>;

export const StandaloneQuoteSaveSchema = Type.Object(
  {
    ...Type.Omit(JobQuoteSaveSchema, [
      "jobVersion",
      "recordedCostBasis",
      "recoveryAmount",
      "recoveryNote",
    ]).properties,
    version: Type.Union([Type.Integer({ minimum: 1 }), Type.Null()]),
    administratorId: Type.String({ format: "uuid" }),
    condominiumId: Type.String({ format: "uuid" }),
  },
  { additionalProperties: false },
);
export type StandaloneQuoteSave = Static<typeof StandaloneQuoteSaveSchema>;
export const QuoteFicActionSchema = Type.Object(
  {
    operationId: Type.String({ format: "uuid" }),
    version: Type.Integer({ minimum: 1 }),
    confirmed: Type.Literal(true),
  },
  { additionalProperties: false },
);
export const QuoteFicSendSchema = Type.Object(
  {
    ...QuoteFicActionSchema.properties,
    recipientEmail: Type.String({ format: "email", maxLength: 254 }),
  },
  { additionalProperties: false },
);
export interface StandaloneQuoteDetail extends JobQuoteDetail {
  version: number;
  administratorId: string;
  condominiumId: string;
  clientName: string;
  recipientEmail: string | null;
  ficExternalId: string | null;
  ficUrl: string | null;
  linkedJobId: string | null;
  ficOperations: Array<{
    id: string;
    kind: "create" | "send" | "update";
    status: "pending" | "succeeded" | "verification-required" | "failed";
    recipientEmail: string | null;
    version: number;
  }>;
}

export const QuoteDetailParamsSchema = Type.Object(
  { id: Type.String({ format: "uuid" }) },
  { additionalProperties: false },
);

export const QuoteConvertToJobSchema = Type.Object(
  {
    quoteVersionId: Type.Optional(Type.String({ format: "uuid" })),
    operationId: Type.String({ format: "uuid" }),
    quoteVersion: Type.Optional(Type.Integer({ minimum: 1 })),
    plannedHours: Type.Optional(
      Type.Number({ minimum: 0, maximum: 999999999999.99 }),
    ),
    administratorId: Type.String({ format: "uuid" }),
    condominiumId: Type.String({ format: "uuid" }),
    ownerEmployeeId: Type.String({ format: "uuid" }),
    openedOn: Type.Optional(Type.String({ format: "date" })),
  },
  { additionalProperties: false },
);

export type QuoteConvertToJob = Static<typeof QuoteConvertToJobSchema>;

export interface JobQuoteLine {
  chapterKey?: string | null;
  hiddenFromCustomer?: boolean;
  id: string;
  lineNumber: number;
  description: string;
  details: string | null;
  quantity: string;
  unit: string | null;
  unitCost: string;
  unitSalePrice: string;
  markupPercent: string;
  netAmount: string;
  costAmount: string;
  isRecovery: boolean;
}

export interface JobQuoteDetail {
  intermediaryPercent?: string;
  intermediaryAmount?: string;
  chapters?: QuoteChapter[];
  id: string;
  syncStatus?: string;
  origin?: string;
  version?: number;
  recordedCostBasis?: string;
  ficExternalId?: string | null;
  ficUrl?: string | null;
  ficOperations?: Array<{
    id: string;
    kind: "create" | "send" | "update";
    status: string;
    version: number;
  }>;
  standalone?: boolean;
  quoteNumber: string;
  subject: string;
  status: string;
  issuedOn: string;
  validUntil: string | null;
  versionNumber: number;
  vatPercent: string;
  netAmount: string;
  budgetAmount: string;
  vatAmount: string;
  totalAmount: string;
  recoveryAmount: string;
  recoveryNote: string | null;
  lines: JobQuoteLine[];
}

export interface JobQuoteResponse {
  quote: JobQuoteDetail | null;
}

export interface EconomicKpis {
  jobsCount: number;
  quotedJobsCount: number;
  quoteNetAmount: string;
  budgetAmount: string;
  expectedRevenue: string;
  laborCost: string;
  externalSourceCost: string;
  internalSourceCost: string;
  totalOperationalSourceCost: string;
  contributionMargin: string;
  operatingMarginAfterInternal: string;
  budgetVariance: string;
  contributionMarginRate: string | null;
  operatingMarginRate: string | null;
  quoteCoverageRate: string | null;
}

export interface EconomicJobRow {
  id: string;
  code: string;
  title: string;
  clientName: string | null;
  status: string;
  jobKind: string;
  quoteNetAmount: string | null;
  budgetAmount: string | null;
  expectedRevenue: string | null;
  laborCost: string;
  externalSourceCost: string;
  totalOperationalSourceCost: string;
  contributionMargin: string | null;
  contributionMarginRate: string | null;
  budgetVariance: string | null;
  externalVatStatus: "known" | "unknown" | "not-applicable";
  subjobCount: number;
}

export interface EconomicHistoryPoint {
  batchId: string;
  capturedAt: string;
  expectedRevenue: string;
  laborCost: string;
  externalSourceCost: string;
  contributionMargin: string;
}

export interface EconomicBreakdown {
  key: string;
  label: string;
  amount: string;
}

export interface EconomicShare extends EconomicBreakdown {
  rate: string | null;
}

export interface DashboardRevenueSummary {
  invoicedAmount: string | null;
  interventionReportsAmount: string | null;
  totalProductionAmount: string | null;
  toInvoiceAmount: string | null;
  collectedAmount: string | null;
  receivableResidual: string | null;
}

export interface DashboardPeriodCosts {
  workedHours: string;
  openingBalanceHours: string;
  openingBalanceCost: string;
  contractualLaborCost: string;
  structureLaborCost: string;
  workedHoursValue: string;
  externalSupplierCost: string;
}

export interface DashboardFicSource {
  configured: boolean;
  available: boolean;
  lastSyncAt: string | null;
  invoiceRows: number;
  creditNoteRows: number;
  interventionReportRows: number;
  receivedExpenseRows: number;
  receivedCreditNoteRows: number;
  linkedFinancialRows: number;
  linkedInvoicedAmount: string | null;
  unattributedInvoicedAmount: string | null;
  note: string;
}

export interface DashboardMonthlyPoint {
  month: number;
  label: string;
  invoicedAmount: string;
  interventionReportsAmount: string;
  totalProductionAmount: string;
  ficRevenueAmount: string;
  passiveDocumentsAmount: string;
  paidLaborCost: string | null;
  /** Contractual cost of recorded CEM hours, by worked-on month through today. */
  contractualLaborCost?: string | null;
  /** Hours from the same entries used for contractual labor cost. */
  contractualLaborHours?: string | null;
  contractualLaborNotes?: string[];
}

export interface DashboardQuarterlyPoint {
  quarter: number;
  label: string;
  ficRevenueAmount: string;
  workedLaborCost: string | null;
  workedLaborHours?: string | null;
  ficSupplierCostAmount: string;
  totalOperatingCost: string | null;
  operatingResult: string | null;
  historicalUnallocatedHours?: string;
  contractualLaborNotes?: string[];
  unallocatedLaborHours?: string;
  unallocatedLaborCost?: string | null;
}

export interface DashboardManagementResult {
  linkedRevenueAmount: string | null;
  linkedCostAmount: string | null;
  linkedProfitAmount: string | null;
  linkedProfitRate: string | null;
  unattributedRevenueAmount: string | null;
}

export interface AdministratorCostBreakdown {
  productiveHours: string;
  manualMaterialsAmount: string;
  contractualLaborAmount: string | null;
  structureAmount: string | null;
  supplierReconciliationAmount: string | null;
}
export interface AdministratorAccounting {
  from: string;
  through: string;
  manualMaterialsAmount: string;
  structureSupplierAmount: string | null;
  internalLaborAmount: string | null;
  internalHours: string;
  supplierReconciliationAmount: string | null;
  totalMarginAmount: string | null;
  totalRevenueAmount: string | null;
  totalMarginRate: string | null;
  reconciled: boolean;
  notes: string[];
}
export interface AdministratorEconomicRow {
  accounting?: AdministratorCostBreakdown;
  key: string;
  name: string;
  managementProfit: string | null;
  managementProfitRate: string | null;
  invoicedAmount: string | null;
  interventionReportsAmount: string | null;
  openJobsWeight: string;
  recoveryAmount: string;
  collectedAmount: string | null;
  receivableResidual: string | null;
}

export interface EconomicDashboard {
  cards?: DashboardCards;
  companyResult?: CompanyManagementResult;
  costBreakdowns?: DashboardCostBreakdowns;
  administratorAccounting?: AdministratorAccounting;
  managementYear: number;
  period: {
    month: number | null;
    from: string;
    toExclusive: string;
    label: string;
  };
  staging: true;
  cutover: false;
  sourceSavedAt: string | null;
  batchFinishedAt: string | null;
  reconciliation: { matched: number; mismatched: number };
  kpis: EconomicKpis;
  vatCoverage: {
    quotedJobsKnown: number;
    quotedJobsUnknown: number;
    externalRowsUnknown: number;
    internalRowsUnknown: number;
  };
  revenue: DashboardRevenueSummary;
  periodCosts: DashboardPeriodCosts;
  fic: DashboardFicSource;
  revenueByAdministrator: EconomicShare[];
  revenueComposition: EconomicShare[];
  monthlyTrend: DashboardMonthlyPoint[];
  quarterlyTrend: DashboardQuarterlyPoint[];
  managementResult: DashboardManagementResult;
  administrators: AdministratorEconomicRow[];
  costMix: EconomicBreakdown[];
  statusMix: { status: string; jobs: number; expectedRevenue: string }[];
  history: EconomicHistoryPoint[];
  jobs: EconomicJobRow[];
  caveats: string[];
}

/** Read-only cost composition. Signed balances retain amounts without pie shares. */
export interface DashboardCostBreakdown {
  periodLabel: string;
  totalAmount: string | null;
  items: EconomicShare[];
  state: "complete" | "partial" | "unavailable";
  notes: string[];
}

export interface DashboardCostBreakdowns {
  structure: DashboardCostBreakdown;
  suppliers: DashboardCostBreakdown;
}

/** Company-wide YTD, independent of job links and of the dashboard month filter.
 * A missing FIC depreciation schedule is not a zero expense.
 */
export interface CompanyManagementResult {
  /** FIC revenue minus all supplier documents and contractual labor, YTD. */
  grossMargin?: DashboardCardMetric & { rate: string | null };
  from: string;
  through: string;
  lastSyncAt: string | null;
  revenueAmount: string | null;
  supplierDocumentsAmount: string | null;
  supplierOperatingCostAmount: string | null;
  /** Same supplier population, partitioned without changing the total. */
  supplierJobCostAmount?: string | null;
  supplierStructureCostAmount?: string | null;
  supplierUnclassifiedCostAmount?: string | null;
  contractualLaborCostAmount: string | null;
  depreciationAmount: string | null;
  depreciationSource?: "confirmed-empty" | "fic" | "unavailable";
  depreciationNote?: string;
  profitAmount: string | null;
  state: "complete" | "partial" | "unavailable";
  notes: string[];
}

export interface InternalCostRow {
  installmentManaged?: boolean;
  budgetRemoved?: boolean;
  simulatorManaged?: boolean;
  structureAllocationAmount?: string;
  warehouseEnabled: boolean;
  active: boolean;
  jobId: string | null;
  budgetVersion: number | null;
  budgetConfigured?: boolean;
  id: string;
  code: string;
  name: string;
  costType: string;
  budgetAmount: string;
  actualAmount: string;
  residualAmount: string;
  hourlyAllocation: string;
  saturationPercent: string | null;
  vatStatus: "known" | "unknown" | "mixed" | "not-applicable";
  sourceRows: number;
  version: number;
}

export const InternalCostCenterCreateSchema = Type.Object(
  {
    managementYear: Type.Optional(
      Type.Integer({ minimum: 2000, maximum: 2200 }),
    ),
    operationId: Type.String({ format: "uuid" }),
    code: Type.Optional(Type.String({ minLength: 1, maxLength: 40 })),
    name: Type.String({ minLength: 1, maxLength: 160 }),
    costType: Type.String({ minLength: 1, maxLength: 60 }),
  },
  { additionalProperties: false },
);

export const InternalCostEntryCreateSchema = Type.Object(
  {
    withoutInvoice: Type.Optional(Type.Boolean()),
    operationId: Type.String({ format: "uuid" }),
    managementYear: Type.Integer({ minimum: 2000, maximum: 2200 }),
    costCenterId: Type.String({ format: "uuid" }),
    incurredOn: Type.String({ format: "date" }),
    amount: Type.Number({ minimum: 0, maximum: 9999999999.99 }),
    description: Type.Optional(
      Type.Union([Type.String({ maxLength: 500 }), Type.Null()]),
    ),
  },
  { additionalProperties: false },
);

export const AnnualBudgetSaveSchema = Type.Object(
  {
    operationId: Type.String({ format: "uuid" }),
    managementYear: Type.Integer({ minimum: 2000, maximum: 2200 }),
    costCenterId: Type.String({ format: "uuid" }),
    budgetAmount: Type.Number({ minimum: 0, maximum: 9999999999.99 }),
    version: Type.Union([Type.Integer({ minimum: 1 }), Type.Null()]),
  },
  { additionalProperties: false },
);

export type InternalCostCenterCreate = Static<
  typeof InternalCostCenterCreateSchema
>;
export type InternalCostEntryCreate = Static<
  typeof InternalCostEntryCreateSchema
>;
export type AnnualBudgetSave = Static<typeof AnnualBudgetSaveSchema>;

export interface InternalJobRow {
  id: string;
  code: string;
  title: string;
  status: string;
  openedOn: string | null;
  plannedAmount: string;
  actualCost: string;
}

export interface InternalCostPage {
  managementYear: number;
  availableYears?: number[];
  /** Annual FIC invoices net of VAT and credit notes; unavailable until synchronized. */
  invoicedAmount?: string | null;
  items: InternalCostRow[];
  internalJobs: InternalJobRow[];
  totals: {
    budgetAmount: string;
    actualAmount: string;
    residualAmount: string;
  };
}

export interface QuoteRegisterItem {
  id: string;
  standalone?: boolean;
  version: number;
  trashed: boolean;
  sourceType: "formal" | "job-aggregate";
  reference: string;
  jobId: string | null;
  jobCode: string | null;
  clientName: string | null;
  administratorName: string | null;
  condominiumName: string | null;
  ownerName: string | null;
  subject: string;
  status: string;
  netAmount: string;
  vatAmount: string | null;
  totalAmount: string | null;
  vatStatus: "known" | "unknown" | "not-applicable";
  versionCount: number;
  issuedOn: string | null;
  marginAmount: string | null;
  marginRate: string | null;
  folderReady: boolean;
  ficLinked: boolean;
  origin: "local" | "legacy" | "fic" | "job-aggregate";
  syncStatus:
    | "local"
    | "synced"
    | "missing-client"
    | "conflict"
    | "deleted-remotely"
    | "pending-update";
  ficExternalId: string | null;
  ficExternalCompanyId: string | null;
  ficUrl: string | null;
  updatedAt: string;
}

export interface QuoteRegisterPage {
  managementYear: number;
  items: QuoteRegisterItem[];
  summary: {
    formalQuotes: number;
    jobAggregates: number;
    netAmount: string;
    vatKnownAmount: string;
    vatKnownRows: number;
    vatUnknownRows: number;
  };
  sourceNote: string;
}

export interface QuoteVersionLineDetail {
  id: string;
  lineNumber: number;
  description: string;
  details?: string | null;
  quantity: string;
  unit: string | null;
  unitPrice: string;
  vatPercent: string | null;
  ficVatId: number | null;
  netAmount: string;
  vatAmount: string | null;
  totalAmount: string | null;
}

export interface QuoteVersionDetail {
  id: string;
  versionNumber: number;
  status: string;
  issuedOn: string | null;
  validUntil: string | null;
  netAmount: string;
  vatAmount: string;
  totalAmount: string;
  createdAt: string;
  lines: QuoteVersionLineDetail[];
}

export interface QuoteDetail {
  id: string;
  quoteNumber: string;
  subject: string;
  description: string | null;
  origin: "local" | "legacy" | "fic";
  syncStatus: QuoteRegisterItem["syncStatus"];
  status: string;
  remoteStatus: string | null;
  issuedOn: string | null;
  validUntil: string | null;
  clientId: string | null;
  clientName: string | null;
  externalClientId: string | null;
  condominiumId: string | null;
  convertedJobId: string | null;
  convertedJobCode: string | null;
  ficExternalId: string | null;
  ficExternalCompanyId: string | null;
  ficUrl: string | null;
  netAmount: string;
  vatAmount: string;
  totalAmount: string;
  versions: QuoteVersionDetail[];
}
